import { useEditor, useNode } from '@craftjs/core';
import { Box, FormControl, FormLabel, Stack, RadioGroup, FormControlLabel, Radio, TextField, Select, MenuItem} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

interface CLinkProps {
  text: any;
  size?: any;
  fontSize?: any;
  fontWeight?: any;
  textTransform?: any;
  textDecoration?: any;
  color?: any;
  link?: any;
  fontFamily?: string;
  [key: string]: any;
}

export const CLink = ({ 
    text, 
    link, 
    fontSize, 
    fontWeight, 
    textTransform, 
    textDecoration, 
    color, 
    fontFamily,
    ...props 
}: CLinkProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));

  const { enabled } = useEditor(
    (state, query) => ({
      enabled: state.options.enabled,
    })
  );


  useEffect(() => {
    if (selected) {
      return;
    }
  }, [selected]);

  return (
    <Box
      {...props}
      className="craft-text"
      ref={(ref: any) => connect(drag(ref))}
      style={{
        borderColor: selected ? '#008522' : '#F0F0F0',
      }}
    >
      <Link
        to={ enabled ? '#' : link}
        dangerouslySetInnerHTML={{ __html: text }}
        style={{ 
          fontSize: `${fontSize}px`, 
          fontWeight: fontWeight,
          textTransform: textTransform,
          textDecoration: textDecoration,
          color: color,
          fontFamily: fontFamily,
        }}
      />
    </Box>
  );
};

const CLinkSettings = () => {
  const {
    actions: { setProp },
    text,
    link,
    fontSize,
    color,
    variant,
    textAlign,
    fontWeight,
    textTransform,
    textDecoration,
    fontFamily,
  } = useNode((node) => ({
    text: node.data.props.text,
    link: node.data.props.link,
    fontSize: node.data.props.fontSize,
    color: node.data.props.color,
    variant: node.data.props.variant,
    textAlign: node.data.props.textAlign,
    fontWeight: node.data.props.fontWeight,
    textTransform: node.data.props.textTransform,
    textDecoration: node.data.props.textDecoration,
    fontFamily: node.data.props.fontFamily,
  }));

  return (
    <Stack spacing={2} paddingBottom={4}>
      <FormControl size="small" component="fieldset" sx={{
        '* > .MuiInputBase-root': {
          height: 'auto !important',
        }
      }}>
        <FormLabel component="legend">Text</FormLabel>
        <TextField
          multiline
          rows={4}
          value={text}
          onChange={(e) =>
            setProp((props: any) => (props.text = e.target.value))
          }
          style={{
            height: 'auto',
          }}
          variant="outlined"
          size="small"
        />
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">Link</FormLabel>
        <TextField
          value={link}
          onChange={(e) =>
            setProp((props: any) => (props.link = e.target.value))
          }
          variant="outlined"
          size="small"
        />
      </FormControl>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Font size</FormLabel>
          <TextField
            value={fontSize}
            onChange={(e) =>
              setProp((props: any) => (props.fontSize = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} variant="outlined">
          <FormLabel component="legend">Font Family</FormLabel>
          <Select
            value={fontFamily}
            onChange={(e) => setProp((props: any) => (props.fontFamily = e.target.value))}
          >
            <MenuItem value="Open Sans,sans-serif">Open Sans</MenuItem>
            <MenuItem value="Arial, sans-serif">Arial</MenuItem>
            <MenuItem value="'Courier New', monospace">Courier New</MenuItem>
            <MenuItem value="'Georgia', serif">Georgia</MenuItem>
            <MenuItem value="'Times New Roman', Times, serif">Times New Roman</MenuItem>
            <MenuItem value="'Trebuchet MS', sans-serif">Trebuchet MS</MenuItem>
            <MenuItem value="'Verdana', sans-serif">Verdana</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <FormControl component="fieldset">
        <FormLabel component="legend">Font Weight</FormLabel>
        <RadioGroup
          defaultValue={fontWeight}
          style={{ flexDirection: 'row', gap: '10px' }}
          onChange={(e) =>
            setProp((props: any) => (props.fontWeight = e.target.value))
          }
        >
          <FormControlLabel
            label="100"
            value="100"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="200"
            value="100"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="300"
            value="300"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="400"
            value="400"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="500"
            value="500"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="600"
            value="600"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="700"
            value="700"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="800"
            value="800"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="900"
            value="900"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">Text Transform</FormLabel>
        <RadioGroup
          defaultValue={textTransform}
          style={{ flexDirection: 'row', gap: '10px' }}
          onChange={(e) =>
            setProp((props: any) => (props.textTransform = e.target.value))
          }
        >
          <FormControlLabel
            label="None"
            value="none"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Capitalize"
            value="capitalize"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Uppercase"
            value="uppercase"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="Lowercase"
            value="lowercase"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">Text Decoration</FormLabel>
        <RadioGroup
          defaultValue={textDecoration}
          style={{ flexDirection: 'row', gap: '10px' }}
          onChange={(e) =>
            setProp((props: any) => (props.textDecoration = e.target.value))
          }
        >
          <FormControlLabel
            label="Underline"
            value="underline"
            control={<Radio size="small" color="primary" />}
          />
          <FormControlLabel
            label="None"
            value="none"
            control={<Radio size="small" color="primary" />}
          />
        </RadioGroup>
      </FormControl>
      <FormControl size="small" component="fieldset">
        <FormLabel component="legend">Color</FormLabel>
        <MuiColorInput format='hex' value={color} onChange={(color) => {
            setProp((props: any) => (props.color = color), 500);
          }}
        />
      </FormControl>
    </Stack>
  );
};

export const CLinkDefaultProps = {
  text: 'Hi',
  fontSize: 16,
  color: '#000000',
  fontWeight: 300,
  textTransform: 'none',
  textDecoration: 'underline',
  // fontFamily: 'Open Sans,sans-serif',
};

CLink.craft = {
  displayName: "Link",
  props: CLinkDefaultProps,
  related: {
    settings: CLinkSettings,
  },
};
